import { useState, useEffect, useRef } from 'react';
import { Menu, X, Search, Phone, Mail, User, ShoppingCart, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/activites?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isSearchOpen && !target.closest('.search-container')) {
        setIsSearchOpen(false);
      }
    };
    if (isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isSearchOpen]);

  const menuItems = [
    {
      label: 'Activités',
      href: '/activites',
      submenu: [
        { label: 'Activités dans le désert', href: '/activites/desert' },
        { label: 'Activités en mer, Yacht & Parcs aquatiques', href: '/activites/mer' },
        { label: 'Activités en ville & Parcs d\'attractions', href: '/activites/parcs' },
        { label: 'Activités pour enfants en bas âge', href: '/activites/famille' },
        { label: 'Activités en couple', href: '/activites/couple' }
      ]
    },
    { label: 'Hôtels', href: '/hotels' },
    {
      label: 'Transport',
      href: '/transport',
      submenu: [
        { label: 'Chauffeur privé', href: '/transport/chauffeur' },
        { label: 'Location de voiture', href: '/transport/location' }
      ]
    },
    { label: 'Nos conseils', href: '/conseils' },
    { label: 'Contact', href: '/contact' }
  ];

  return (
    <header className={`transition-all duration-300 ${
      isScrolled 
        ? 'fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-lg' 
        : 'relative bg-white/90'
    }`}>
      {/* Top bar */}
      <div className="bg-blue-900 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center space-x-6">
            <a href="tel:+971506211791" className="flex items-center space-x-2 hover:text-amber-300 transition-colors">
              <Phone size={16} />
              <span>+971 50 62 11 791</span>
            </a>
            <a href="mailto:contact@mvd.perle-in.com" className="flex items-center space-x-2 hover:text-amber-300 transition-colors">
              <Mail size={16} />
              <span>contact@mvd.perle-in.com</span>
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-amber-300">🏆 +100k clients satisfaits</span>
            <div className="flex space-x-2">
              <a href="https://www.facebook.com/mesvacancesadubai" target="_blank" rel="noopener noreferrer" className="hover:text-amber-300 transition-colors">
                <i className="fab fa-facebook"></i>
              </a>
              <a href="https://www.instagram.com/mesvacancesadubai" target="_blank" rel="noopener noreferrer" className="hover:text-amber-300 transition-colors">
                <i className="fab fa-instagram"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://mesvacancesadubai.com/wp-content/uploads/2021/02/MVD-Logo-Dubai-Color.png" 
                alt="Mes Vacances à Dubaï" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {menuItems.map((item) => (
              <div 
                key={item.label}
                className="relative"
                onMouseEnter={() => item.submenu && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className="flex items-center space-x-1 text-gray-700 hover:text-amber-600 font-medium transition-colors py-2"
                >
                  <span>{item.label}</span>
                  {item.submenu && <ChevronDown size={16} />}
                </Link>
                
                {item.submenu && activeDropdown === item.label && (
                  <div 
                    className="absolute top-full left-0 pt-1 w-64 z-50"
                    onMouseEnter={() => setActiveDropdown(item.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {/* Zone de transition invisible pour combler l'espace */}
                    <div className="h-1"></div>
                    <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.label}
                          to={subItem.href}
                          className="block px-4 py-3 text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            <div className="relative search-container">
              <button 
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-2 text-gray-700 hover:text-amber-600 transition-colors"
              >
                <Search size={20} />
              </button>
              
              {/* Search Bar */}
              {isSearchOpen && (
                <div className="absolute right-0 top-full mt-3 w-[420px] bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <form onSubmit={handleSearch} className="relative">
                    <div className="flex items-center px-5 py-4 bg-gradient-to-r from-amber-50 to-white border-b border-gray-100">
                      <div className="flex items-center flex-1 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-500/20 transition-all">
                        <Search size={20} className="text-amber-600 mr-3 flex-shrink-0" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Rechercher une activité, un lieu..."
                          className="flex-1 outline-none text-gray-900 placeholder:text-gray-400 text-sm"
                        />
                        {searchQuery && (
                          <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="ml-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                    {searchQuery && (
                      <div className="px-5 py-3 bg-gray-50/50 flex items-center justify-between border-t border-gray-100">
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <kbd className="px-2 py-1 bg-white border border-gray-300 rounded-md text-xs font-mono shadow-sm">Entrée</kbd>
                          <span>pour rechercher</span>
                        </span>
                        <button
                          type="submit"
                          className="px-5 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-xl hover:from-amber-700 hover:to-amber-800 transition-all text-sm font-semibold flex items-center gap-2 shadow-md hover:shadow-lg"
                        >
                          <Search size={16} />
                          Rechercher
                        </button>
                      </div>
                    )}
                    {!searchQuery && (
                      <div className="px-5 py-4 bg-gray-50/30">
                        <p className="text-xs text-gray-500 text-center">
                          Commencez à taper pour rechercher des activités
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              )}
            </div>
            <a href="/mon-compte" className="p-2 text-gray-700 hover:text-amber-600 transition-colors">
              <User size={20} />
            </a>
            <a href="/panier" className="p-2 text-gray-700 hover:text-amber-600 transition-colors relative">
              <ShoppingCart size={20} />
              <span className="absolute -top-1 -right-1 bg-amber-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">0</span>
            </a>
            
            <Button size="sm" className="hidden lg:inline-flex">
              Réserver maintenant
            </Button>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-amber-600 transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-4">
            {menuItems.map((item) => (
              <div key={item.label}>
                <Link
                  to={item.href}
                  className="block text-gray-700 hover:text-amber-600 font-medium py-2"
                >
                  {item.label}
                </Link>
                {item.submenu && (
                  <div className="ml-4 space-y-2">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.label}
                        to={subItem.href}
                        className="block text-gray-600 hover:text-amber-600 py-1"
                      >
                        {subItem.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 border-t border-gray-200">
              <Button className="w-full">
                Réserver maintenant
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}